import api from './api.service';
import type {
  DocumentTemplate,
  DocumentTemplateListItem,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  DocumentFilling,
  FilledField,
  ApproveFieldRequest,
  ManualFillFieldRequest,
  PermanentDeleteResponse,
} from '@/types/template.types';

// === TEMPLATES API ===

export const templatesApi = {
  /**
   * Get all templates (admin/analyst only)
   */
  getAll: async (includeInactive = false): Promise<DocumentTemplateListItem[]> => {
    const { data } = await api.get<DocumentTemplateListItem[]>('/api/templates', {
      params: { include_inactive: includeInactive },
    });
    return data;
  },

  /**
   * Get active templates for room creation dropdown
   */
  getActiveForSelection: async (): Promise<DocumentTemplateListItem[]> => {
    const { data } = await api.get<DocumentTemplateListItem[]>('/api/templates/active');
    return data;
  },

  /**
   * Get template by ID with all fields
   */
  getById: async (id: number): Promise<DocumentTemplate> => {
    const { data } = await api.get<DocumentTemplate>(`/api/templates/${id}`);
    return data;
  },

  /**
   * Get version history for a template
   */
  getVersions: async (id: number): Promise<DocumentTemplateListItem[]> => {
    const { data } = await api.get<DocumentTemplateListItem[]>(`/api/templates/${id}/versions`);
    return data;
  },

  /**
   * Create a new template (admin/analyst only)
   */
  create: async (templateData: CreateTemplateRequest): Promise<DocumentTemplate> => {
    const { data } = await api.post<DocumentTemplate>('/api/templates', templateData);
    return data;
  },

  /**
   * Update a template (creates new version)
   */
  update: async (id: number, templateData: UpdateTemplateRequest): Promise<DocumentTemplate> => {
    const { data } = await api.put<DocumentTemplate>(`/api/templates/${id}`, templateData);
    return data;
  },

  /**
   * Deactivate a template (admin only)
   */
  deactivate: async (id: number): Promise<void> => {
    await api.delete(`/api/templates/${id}`);
  },

  /**
   * Permanently delete a template and all related data (admin only)
   * WARNING: This action is irreversible!
   */
  deletePermanently: async (id: number): Promise<PermanentDeleteResponse> => {
    const { data } = await api.delete<PermanentDeleteResponse>(`/api/templates/${id}/permanent`);
    return data;
  },
};

// === DOCUMENT FILLING API ===

export const fillingApi = {
  /**
   * Get document filling for a room
   */
  getByRoomCode: async (roomCode: string): Promise<DocumentFilling> => {
    const { data } = await api.get<DocumentFilling>(`/api/rooms/${roomCode}/filling`);
    return data;
  },

  /**
   * Approve a field suggestion
   */
  approveField: async (
    roomCode: string,
    fieldId: number,
    request?: ApproveFieldRequest
  ): Promise<FilledField> => {
    const { data } = await api.post<FilledField>(
      `/api/rooms/${roomCode}/filling/fields/${fieldId}/approve`,
      request || {}
    );
    return data;
  },

  /**
   * Reject a field suggestion
   */
  rejectField: async (roomCode: string, fieldId: number): Promise<FilledField> => {
    const { data } = await api.post<FilledField>(
      `/api/rooms/${roomCode}/filling/fields/${fieldId}/reject`
    );
    return data;
  },

  /**
   * Manually fill a field
   */
  fillField: async (
    roomCode: string,
    fieldId: number,
    request: ManualFillFieldRequest
  ): Promise<FilledField> => {
    const { data } = await api.post<FilledField>(
      `/api/rooms/${roomCode}/filling/fields/${fieldId}/fill`,
      request
    );
    return data;
  },

  /**
   * Mark filling as complete
   */
  complete: async (roomCode: string): Promise<DocumentFilling> => {
    const { data } = await api.post<DocumentFilling>(
      `/api/rooms/${roomCode}/filling/complete`
    );
    return data;
  },

  /**
   * Clear a filled field value, returning it to pending status
   */
  clearField: async (roomCode: string, fieldId: number): Promise<void> => {
    await api.post(`/api/agent/${roomCode}/fields/${fieldId}/clear`);
  },

  /**
   * Generate a document from the filled fields
   * Returns the document as a downloadable blob
   */
  generateDocument: async (
    roomCode: string,
    format: 'pdf' | 'docx' = 'pdf',
    includeSummary: boolean = true
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await api.post(
      `/api/rooms/${roomCode}/filling/generate-document`,
      { format, includeSummary },
      { responseType: 'blob' }
    );

    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers['content-disposition'];
    let filename = `documento.${format}`;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="(.+)"/);
      if (match) {
        filename = match[1];
      }
    }

    return { blob: response.data, filename };
  },
};
